import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits
} from '../../../../types';
import {
  FetchAccountsCacheKey,
  FetchFinAccountsFilters,
  FetchAccountsSort,
  FetchAccountsPage,
  FetchAccountsLimit,
  FetchAccountsGqlQuery
} from '../../accountsTypes';
import { InfiniteIndexQueryBaseNodeType } from '../../../common/reactQuery/hooks/useInfiniteIndexQuery';

import {
  FETCH_CACHE_ACCOUNTS_QUERY,
  FetchCacheAccountsQueryResponse
} from '../../queries/fetchCacheAccounts.query';

import { useCacheInfiniteIndexQuery } from '../../../common/reactQuery/hooks/useCacheInfiniteIndexQuery';

import {
  INITIAL_ACCOUNTS_FILTERS,
  INITIAL_ACCOUNTS_LIMIT,
  INITIAL_ACCOUNTS_SORT
} from '../../accountsConstants';

interface CacheAccountsOptions {
  action?: string;
  cacheKey: FetchAccountsCacheKey;
  query?: FetchAccountsGqlQuery;
  cacheQuery?: FetchAccountsGqlQuery;
  initialFilters?: FetchFinAccountsFilters;
  initialSort?: FetchAccountsSort;
  initialPage?: FetchAccountsPage;
  initialLimit?: FetchAccountsLimit;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  source?: FetchCacheItemsSources;
}

const scope = 'accounts';

function useCacheAccounts<
  ItemType extends InfiniteIndexQueryBaseNodeType = FetchCacheAccountsQueryResponse
>({
  action,
  cacheKey,
  query = FETCH_CACHE_ACCOUNTS_QUERY,
  cacheQuery,
  initialFilters = INITIAL_ACCOUNTS_FILTERS,
  initialSort = INITIAL_ACCOUNTS_SORT,
  initialLimit = INITIAL_ACCOUNTS_LIMIT,
  options = {},
  trackTotalHits,
  source = FetchCacheItemsSources.DB
}: CacheAccountsOptions) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isFetchingNextPage,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    hasNextPage,
    updateItemCache,
    loadMoreItems,
    filterItems,
    changeItemsFilters,
    clearItemsFilters,
    clearItemsFiltersPersistInitial,
    sortItems,
    limitItems
  } = useCacheInfiniteIndexQuery<ItemType>({
    action,
    defaultApi: 'cache_api',
    scope,
    query,
    cacheQuery: cacheQuery || query,
    cacheKey,
    initialFilters,
    initialLimit,
    initialSort,
    options,
    trackTotalHits,
    source
  });

  return {
    accountsData: data,
    accounts: items,
    accountsError: itemsError,
    accountsTotalCount: itemsTotalCount,
    accountsFetched: isFetched,
    accountsLoading: isLoading,
    accountsFetchingNextPage: isFetchingNextPage,
    accountsIsPlaceholderData: isPlaceholderData,
    accountsFilters: currentFilters,
    accountsSort: currentSort,
    accountsPage: currentPage,
    accountsLimit: currentLimit,
    hasNextAccountsPage: hasNextPage,
    updateAccountCache: updateItemCache,
    loadMoreAccounts: loadMoreItems,
    filterAccounts: filterItems,
    changeAccountsFilters: changeItemsFilters,
    clearAccountsFilters: clearItemsFilters,
    clearAccountsFiltersPersistInitial: clearItemsFiltersPersistInitial,
    sortAccounts: sortItems,
    limitAccounts: limitItems
  };
}

export default useCacheAccounts;
